import React from 'react';
import { useQuery } from '@tanstack/react-query';
import portfolioService, { RiskData, WindowRisk } from '../../services/portfolio';
import RiskAttribution from './RiskAttribution';
import LoadingSpinner from '../ui/LoadingSpinner';
import InfoTooltip from '../ui/InfoTooltip';

interface Props {
  portfolioId: number;
}

const pct = (v: number) => `${(v * 100).toFixed(0)}%`;

// Inline monospace formula chip used inside the consolidated info box.
const f = (s: string) => (
  <code style={{ fontFamily: 'monospace', fontSize: '0.68rem', background: '#f4f4f5', borderRadius: '3px', padding: '1px 4px', color: '#18181b' }}>{s}</code>
);

// Per-metric value formatters. All take a non-null number.
const fmtPctSigned = (v: number) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;
const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;
const fmtLoss = (v: number) => `−${(v * 100).toFixed(1)}%`;   // VaR/ES — reported positive, shown as a loss
const fmtNum = (v: number) => v.toFixed(2);

interface RiskRow {
  label: string;
  info: React.ReactNode;
  key: keyof WindowRisk;
  fmt: (v: number) => string;
}

interface RiskGroup {
  heading: string;
  rows: RiskRow[];
}

const GROUPS: RiskGroup[] = [
  {
    heading: 'Return & risk-adjusted',
    rows: [
      { label: 'Annualized return', key: 'annualized_return', fmt: fmtPctSigned,
        info: <>Geometric mean daily return, annualized {f('(Π(1+rₜ))^(252/n)−1')}.</> },
      { label: 'Volatility', key: 'volatility', fmt: fmtPct,
        info: <>Annualized σ of daily returns {f('σ×√252')} — how much the book swings.</> },
      { label: 'Sharpe ratio', key: 'sharpe', fmt: fmtNum,
        info: <>Excess return per unit of total volatility {f('(Rₐ−r_f)/σ')}; &gt; 1 is generally good.</> },
      { label: 'Sortino ratio', key: 'sortino', fmt: fmtNum,
        info: <>Like Sharpe but per unit of <em>downside</em> deviation — penalizes only harmful volatility.</> },
    ],
  },
  {
    heading: 'Market sensitivity',
    rows: [
      { label: 'Beta', key: 'beta', fmt: fmtNum,
        info: <>Sensitivity to the benchmark {f('Cov(r_p,r_b)/Var(r_b)')}; &lt; 1 defensive, &gt; 1 amplifies.</> },
      { label: 'Correlation', key: 'correlation', fmt: fmtNum,
        info: <>Linear co-movement with the benchmark (−1 to +1).</> },
      { label: 'R²', key: 'r_squared', fmt: fmtNum,
        info: <>Share of the portfolio's variance explained by the benchmark {f('corr²')}.</> },
    ],
  },
  {
    heading: 'Tail & drawdown risk',
    rows: [
      { label: 'Max drawdown', key: 'max_drawdown', fmt: fmtPct,
        info: <>Largest peak-to-trough decline over the window {f('min(cumₜ/peakₜ−1)')}.</> },
      { label: 'Current drawdown', key: 'current_drawdown', fmt: fmtPct,
        info: <>How far below the all-time peak the book sits right now.</> },
      { label: 'VaR 95% (1-day)', key: 'var_95', fmt: fmtLoss,
        info: <>Historical 1-day Value-at-Risk: the daily loss exceeded only 5% of days {f('−quantile(r,5%)')}.</> },
      { label: 'VaR 99% (1-day)', key: 'var_99', fmt: fmtLoss,
        info: <>Historical 1-day Value-at-Risk at the 1% tail.</> },
      { label: 'Expected shortfall 95%', key: 'es_95', fmt: fmtLoss,
        info: <>Average loss on the worst 5% of days (conditional VaR) — captures tail severity beyond VaR.</> },
    ],
  },
];

const groupLabelStyle: React.CSSProperties = {
  fontSize: '0.66rem', fontWeight: 600, color: '#a1a1aa',
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px',
};
const infoLineStyle: React.CSSProperties = { fontSize: '0.72rem', color: '#52525b', lineHeight: 1.5 };

interface Provenance {
  positionsCovered: number;
  positionsTotal: number;
  weightCovered: number;
  benchmarkDays: number;
  asOf: string;
}

const buildInfoBox = (benchmark: string, riskFree: number, prov: Provenance) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
    {GROUPS.map(group => (
      <div key={group.heading}>
        <div style={groupLabelStyle}>{group.heading}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {group.rows.map(row => (
            <div key={row.label} style={infoLineStyle}>
              <strong style={{ color: '#18181b' }}>{row.label}</strong> — {row.info}
            </div>
          ))}
        </div>
      </div>
    ))}
    <div style={{ borderTop: '1px solid #f4f4f5', paddingTop: '9px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={groupLabelStyle}>Inputs</div>
      <div style={infoLineStyle}>
        <strong style={{ color: '#18181b' }}>Benchmark — {benchmark}</strong> — beta, correlation and R² are
        measured against the iShares MSCI ACWI ETF, a broad global-equity index spanning developed and
        emerging markets. The natural reference for an internationally diversified book.
      </div>
      <div style={infoLineStyle}>
        <strong style={{ color: '#18181b' }}>Risk-free rate — {(riskFree * 100).toFixed(1)}%</strong> — the
        baseline return subtracted in Sharpe and Sortino, standing in for what cash or short government bills
        would earn.
      </div>
    </div>
    <div style={{ borderTop: '1px solid #f4f4f5', paddingTop: '9px', display: 'flex', flexWrap: 'wrap', gap: '10px 24px' }}>
      {([
        ['Positions covered', `${prov.positionsCovered} / ${prov.positionsTotal}`],
        ['Weight covered', `${(prov.weightCovered * 100).toFixed(0)}%`],
        ['Benchmark', `${benchmark} · ${prov.benchmarkDays} days`],
        ['As of', prov.asOf],
      ] as [string, string][]).map(([label, value]) => (
        <div key={label}>
          <div style={{ fontSize: '0.66rem', color: '#a1a1aa', marginBottom: '1px' }}>{label}</div>
          <div style={{ fontSize: '0.72rem', color: '#18181b', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
        </div>
      ))}
    </div>
  </div>
);

const RiskSection: React.FC<Props> = ({ portfolioId }) => {
  const { data, isLoading, isError } = useQuery<RiskData>({
    queryKey: ['portfolio-risk', portfolioId],
    queryFn: () => portfolioService.getPortfolioRisk(portfolioId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="card card-forest" style={{ minHeight: 200 }}>
        <div className="card-body d-flex align-items-center justify-content-center">
          <LoadingSpinner size="sm" text="Computing risk metrics..." />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="card card-forest">
        <div className="card-body p-4" style={{ color: '#71717a', fontSize: '0.875rem' }}>
          Analytical service unavailable — risk metrics could not be loaded.
        </div>
      </div>
    );
  }

  const { windows, coverage, benchmark, as_of, risk_free_rate } = data;
  const asOf = new Date(as_of).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const hasAnyData = windows.some(w => w.volatility !== null);

  const cols = `1fr ${windows.map(() => '88px').join(' ')}`;

  return (
    <>
    <div className="card card-forest">
      <div className="card-body p-3 p-md-4">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="fw-semibold" style={{ fontSize: '0.95rem', color: '#000537' }}>Risk</span>
          <span className="d-flex align-items-center" style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>
            vs {benchmark} · rf {(risk_free_rate * 100).toFixed(1)}%
            <InfoTooltip
              text={buildInfoBox(benchmark, risk_free_rate, {
                positionsCovered: coverage.positions_covered,
                positionsTotal: coverage.positions_total,
                weightCovered: coverage.weight_covered,
                benchmarkDays: coverage.benchmark_days,
                asOf,
              })}
              width={460}
              placement="bottom"
            />
          </span>
        </div>

        {!hasAnyData ? (
          <div style={{ color: '#a1a1aa', fontSize: '0.875rem', padding: '12px 0' }}>
            Not enough price history to compute risk metrics yet
            {coverage.weight_covered > 0 ? ` (covering ${pct(coverage.weight_covered)} of the book).` : '.'}
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: cols, gap: '0 16px', alignItems: 'center' }}>
              {/* Header */}
              <div style={{ fontSize: '0.72rem', color: '#a1a1aa', paddingBottom: '10px', borderBottom: '1px solid #f4f4f5' }}>Metric</div>
              {windows.map(w => (
                <div key={w.window_years} style={{ fontSize: '0.72rem', color: '#a1a1aa', textAlign: 'right', paddingBottom: '10px', borderBottom: '1px solid #f4f4f5' }}>
                  {w.window_years}Y
                </div>
              ))}

              {GROUPS.map(group => (
                <React.Fragment key={group.heading}>
                  <div style={{ gridColumn: '1 / -1', fontSize: '0.68rem', fontWeight: 600, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '12px 0 4px' }}>
                    {group.heading}
                  </div>
                  {group.rows.map(row => (
                    <React.Fragment key={row.label}>
                      <div style={{ fontSize: '0.82rem', color: '#52525b', padding: '7px 0', borderBottom: '1px solid #f9f9f9' }}>
                        {row.label}
                      </div>
                      {windows.map(w => {
                        const v = w[row.key] as number | null;
                        return (
                          <div key={w.window_years} style={{ fontSize: '0.82rem', color: v !== null ? '#18181b' : '#a1a1aa', textAlign: 'right', padding: '7px 0', borderBottom: '1px solid #f9f9f9', fontVariantNumeric: 'tabular-nums' }}>
                            {v !== null ? row.fmt(v) : '—'}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}

              {/* Window length footnote row */}
              <div style={{ fontSize: '0.72rem', color: '#a1a1aa', padding: '10px 0 0' }}>Trading days used</div>
              {windows.map(w => (
                <div key={w.window_years} style={{ fontSize: '0.72rem', color: '#a1a1aa', textAlign: 'right', padding: '10px 0 0', fontVariantNumeric: 'tabular-nums' }}>
                  {w.days_used}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
    {hasAnyData && data.attribution && <RiskAttribution data={data.attribution} />}
    </>
  );
};

export default RiskSection;
